import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, CreditCard, Shield, Monitor, Users, ChevronRight, Pencil, Lock, Languages, ShieldAlert, Subtitles, Play, Eye, EyeOff, Clock, FileText, Trash2, AlertCircle, Ban, Tv, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CancelSurveyModal } from '../components/CancelSurveyModal';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SubscriptionSection({ navigate }) {
  const [subInfo, setSubInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);

  const loadSub = useCallback(() => {
    axios.get(`${API}/subscribe/me`, { withCredentials: true }).then(r => setSubInfo(r.data)).catch(() => setSubInfo({}));
  }, []);
  useEffect(() => { loadSub(); }, [loadSub]);

  const cancelSub = () => setShowSurvey(true);

  const confirmCancel = async (reason, comment) => {
    setBusy(true);
    try { await axios.post(`${API}/subscribe/cancel`, { reason, comment }, { withCredentials: true }); setShowSurvey(false); loadSub(); }
    catch (err) { alert(err.response?.data?.detail || 'Résiliation impossible'); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/subscribe/reactivate`, {}, { withCredentials: true }); loadSub(); }
    catch (err) { alert(err.response?.data?.detail || 'Reprise impossible'); }
    finally { setBusy(false); }
  };

  const active = subInfo && ['active', 'trialing'].includes(subInfo.status);
  return (
    <div>
      {showSurvey && <CancelSurveyModal busy={busy} onConfirm={confirmCancel} onClose={() => setShowSurvey(false)} onSaved={loadSub} />}
      <h2 className="text-3xl font-extrabold text-white mb-6">Abonnement</h2>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
        <p className="text-sm text-white/50 mb-3">Votre abonnement actuel</p>
        {!subInfo ? <p className="text-white/40 text-sm">Chargement…</p> : (
          <>
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <p className="text-2xl font-bold text-white capitalize" data-testid="settings-sub-plan">{subInfo.plan_name || subInfo.plan_id || 'Gratuit'}</p>
              {active && !subInfo.cancel_at_period_end && <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30">ACTIF</span>}
              {subInfo.cancel_at_period_end && <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">RÉSILIATION PROGRAMMÉE</span>}
            </div>
            {subInfo.current_period_end && (
              <p className="text-sm text-white/50">
                {subInfo.cancel_at_period_end
                  ? `Accès jusqu'au ${new Date(subInfo.current_period_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                  : `Prochain renouvellement le ${new Date(subInfo.current_period_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`}
              </p>
            )}
            <div className="flex items-center gap-3 flex-wrap mt-5">
              <button onClick={() => navigate('/account')} className="px-6 py-2.5 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-semibold text-sm transition-colors" data-testid="settings-manage-sub-btn">
                Gérer l'abonnement
              </button>
              {subInfo.has_stripe_subscription && subInfo.cancel_at_period_end && (
                <button onClick={reactivate} disabled={busy} data-testid="settings-reactivate-btn"
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/10 rounded-lg transition-colors disabled:opacity-50">
                  {busy ? '…' : "Reprendre l'abonnement"}
                </button>
              )}
              {subInfo.has_stripe_subscription && active && !subInfo.cancel_at_period_end && (
                <button onClick={cancelSub} disabled={busy} data-testid="settings-cancel-sub-btn"
                  className="px-5 py-2.5 text-sm font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors disabled:opacity-50">
                  {busy ? '…' : "Résilier l'abonnement"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


const KIND_LABEL = { tv: 'Téléviseur', mobile: 'Mobile / tablette', web: 'Navigateur web' };

function DevicesTab() {
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const mySid = sessionStorage.getItem('pb_sid');

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/playback/sessions`, { withCredentials: true }).then(r => setSessions(r.data.sessions || [])).catch(() => {}),
      axios.get(`${API}/tv/devices`, { withCredentials: true }).then(r => setDevices(r.data || [])).catch(() => {}),
    ]).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const revoke = async (sid) => {
    setRevoking(sid);
    try {
      await axios.post(`${API}/playback/sessions/${sid}/revoke`, {}, { withCredentials: true });
      setSessions(s => s.filter(x => x.session_id !== sid));
    } catch { /* ignore */ }
    setRevoking(null);
  };

  const removeDevice = async (d) => {
    if (d.is_current && !window.confirm('Cet appareil est celui que vous utilisez actuellement : vous serez déconnecté. Continuer ?')) return;
    setRemoving(d.id);
    try {
      await axios.delete(`${API}/tv/devices/${d.id}`, { withCredentials: true });
      if (d.is_current) { window.location.href = '/login'; return; }
      setDevices(list => list.filter(x => x.id !== d.id));
    } catch { /* ignore */ }
    setRemoving(null);
  };

  const disconnectOthers = async () => {
    if (!window.confirm('Déconnecter votre compte de tous les autres appareils (téléviseurs, mobiles, navigateurs) ? Leur lecture en cours sera arrêtée.')) return;
    setBusyAll(true);
    try { await axios.post(`${API}/tv/devices/disconnect-others`, {}, { withCredentials: true }); load(); } catch { /* ignore */ }
    setBusyAll(false);
  };

  const fmtSince = (iso) => {
    try {
      const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
      if (mins < 1) return "à l'instant";
      if (mins < 60) return `il y a ${mins} min`;
      if (mins < 1440) return `il y a ${Math.floor(mins / 60)} h`;
      return `il y a ${Math.floor(mins / 1440)} j`;
    } catch { return ''; }
  };

  const kindIcon = (kind) => kind === 'tv' ? Tv : kind === 'mobile' ? Smartphone : Monitor;

  return (
    <div data-testid="devices-tab">
      <h2 className="text-3xl font-extrabold text-white mb-2">Appareils connectés</h2>
      <p className="text-sm text-white/40 mb-6">Tous les appareils connectés à votre compte. Déconnectez un appareil pour fermer sa session : sa lecture s'arrête et il devra se reconnecter.</p>
      {!loaded ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6"><p className="text-sm text-white/40">Chargement…</p></div>
      ) : (
        <>
          {devices.length === 0 ? (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center" data-testid="devices-empty">
              <Monitor size={32} className="text-white/20 mx-auto mb-3" />
              <p className="text-sm text-white/50">Aucun appareil connecté à votre compte.</p>
            </div>
          ) : (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5" data-testid="account-devices-list">
              {devices.map(d => {
                const Icon = kindIcon(d.kind);
                return (
                  <div key={d.id} className="flex items-center gap-4 px-6 py-5" data-testid={`account-device-${d.id}`}>
                    <div className="w-11 h-11 rounded-full bg-white/8 flex items-center justify-center flex-none">
                      <Icon size={18} className="text-white/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {d.name || KIND_LABEL[d.kind] || 'Appareil'}
                        {d.is_current && <span className="ml-2 text-[10px] font-bold text-[#E9641A] uppercase">Cet appareil</span>}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5 truncate">{KIND_LABEL[d.kind] || 'Appareil'} · dernière activité {fmtSince(d.last_seen)}</p>
                    </div>
                    <button onClick={() => removeDevice(d)} disabled={removing === d.id}
                      className="px-4 py-2 text-xs font-semibold rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-red-500/60 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                      data-testid={`disconnect-device-${d.id}`}>
                      {removing === d.id ? '…' : 'Déconnecter'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {devices.filter(d => !d.is_current).length > 0 && (
            <button onClick={disconnectOthers} disabled={busyAll}
              className="mt-4 px-5 py-3 text-sm font-bold rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
              data-testid="disconnect-all-others-btn">
              {busyAll ? 'Déconnexion…' : 'Déconnecter tous les autres appareils'}
            </button>
          )}

          <h3 className="text-lg font-bold text-white mt-10 mb-2">Lectures en cours</h3>
          <p className="text-sm text-white/40 mb-4">Écrans en train de regarder un contenu. Vous pouvez arrêter une lecture à distance : elle s'arrêtera sous une minute.</p>
          {sessions.length === 0 ? (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center" data-testid="sessions-empty">
              <Play size={28} className="text-white/20 mx-auto mb-3" />
              <p className="text-sm text-white/50">Aucune lecture en cours sur votre compte.</p>
            </div>
          ) : (
            <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
              {sessions.map(s => (
                <div key={s.session_id} className="flex items-center gap-4 px-6 py-5" data-testid={`device-session-${s.session_id}`}>
                  <div className="w-11 h-11 rounded-full bg-[#E9641A]/15 flex items-center justify-center flex-none">
                    <Play size={18} className="text-[#E9641A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {s.profile_name} regarde {s.content_title ? `« ${s.content_title} »` : 'un contenu'}
                      {s.session_id === mySid && <span className="ml-2 text-[10px] font-bold text-[#E9641A] uppercase">Cet appareil</span>}
                    </p>
                    <p className="text-xs text-white/40 mt-0.5 truncate">{s.device} · {s.city}</p>
                  </div>
                  <button onClick={() => revoke(s.session_id)} disabled={revoking === s.session_id}
                    className="px-4 py-2 text-xs font-semibold rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-red-500/60 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    data-testid={`revoke-session-${s.session_id}`}>
                    {revoking === s.session_id ? '…' : 'Arrêter la lecture'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

const SIDEBAR_ITEMS = [
  { id: 'overview', label: 'Présentation', icon: Home },
  { id: 'subscription', label: 'Abonnement', icon: CreditCard },
  { id: 'security', label: 'Sécurité', icon: Shield },
  { id: 'devices', label: 'Appareils', icon: Monitor },
  { id: 'profiles', label: 'Profils', icon: Users },
];

const LANGUAGES = [
  'العربية', 'Deutsch', 'English', 'Español', 'Français', 'Italiano',
  'Nederlands', 'Português', 'Türkçe', 'Polski', 'Svenska', 'Norsk',
];

export default function AccountSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'profiles');
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [subPage, setSubPage] = useState(null); // 'manage' | 'languages' | 'lock' | 'parental'
  const [avatarReturn, setAvatarReturn] = useState('edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [editName, setEditName] = useState('');
  const [displayLang, setDisplayLang] = useState('Français');
  const [audioLangs, setAudioLangs] = useState(['Français']);
  const [maxAge, setMaxAge] = useState('18+');
  const [subtitleSize, setSubtitleSize] = useState('Moyen');
  const [subtitleFont, setSubtitleFont] = useState('Block');
  const [subtitleShadow, setSubtitleShadow] = useState('Ombre portée');
  const [subtitleBg, setSubtitleBg] = useState('Sans');
  const [autoplayNext, setAutoplayNext] = useState(true);
  const [autoplayPreviews, setAutoplayPreviews] = useState(true);
  const [dataUsage, setDataUsage] = useState('Auto');
  const [watchHistoryItems, setWatchHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [consentAds, setConsentAds] = useState(true);
  const [behavioralAds, setBehavioralAds] = useState(true);
  const [gender, setGender] = useState('');
  const [profileFilters, setProfileFilters] = useState({});
  const { user, activeProfile, checkAuth } = useAuth();
  const navigate = useNavigate();

  const fetchProfiles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  // Deep link: if ?profile=xxx, auto-open that profile's manage page
  useEffect(() => {
    const profileParam = searchParams.get('profile');
    if (profileParam && profiles.length > 0 && !selectedProfile) {
      const p = profiles.find(pr => pr.id === profileParam);
      if (p) openProfileManage(p);
    }
  }, [profiles, searchParams, selectedProfile]);

  // Fetch avatar gallery from admin
  const [avatarCategories, setAvatarCategories] = useState([]);
  const [adminFilters, setAdminFilters] = useState([]);
  useEffect(() => {
    axios.get(`${API}/avatar-gallery`, { withCredentials: true })
      .then(r => setAvatarCategories(r.data))
      .catch(() => {});
    axios.get(`${API}/filters`, { withCredentials: true })
      .then(r => setAdminFilters(r.data))
      .catch(() => {});
  }, []);

  const changeTab = (id) => {
    setTab(id);
    setSubPage(null);
    setSelectedProfile(null);
    setSearchParams({ tab: id });
  };

  const openProfileManage = (profile) => {
    setSelectedProfile(profile);
    setSubPage('manage');
    setEditName(profile.name);
    setDisplayLang(profile.display_language || 'Français');
    setAudioLangs(profile.preferred_languages || ['Français']);
    setMaxAge(profile.max_age_rating || '18+');
    setGender(profile.gender || '');
    const dynamicDefaults = {};
    adminFilters.forEach(f => { dynamicDefaults[f.id] = f.id !== 'violence'; });
    setProfileFilters({ ...dynamicDefaults, ...(profile.default_filters || {}) });
  };

  const saveProfileName = async () => {
    if (!editName.trim() || !selectedProfile) return;
    setSaving(true);
    try {
      await axios.put(`${API}/profiles/${selectedProfile.id}`, { name: editName }, { withCredentials: true });
      await fetchProfiles();
      setSelectedProfile(prev => ({ ...prev, name: editName }));
    } catch {}
    setSaving(false);
  };

  const savePIN = async () => {
    if (pinInput.length !== 4 || !selectedProfile) return;
    setSaving(true);
    try {
      await axios.put(`${API}/profiles/${selectedProfile.id}`, { pin: pinInput }, { withCredentials: true });
      await fetchProfiles();
      setSubPage('manage');
      setPinInput('');
    } catch {}
    setSaving(false);
  };

  const removePIN = async () => {
    if (!selectedProfile) return;
    setSaving(true);
    try {
      await axios.put(`${API}/profiles/${selectedProfile.id}`, { pin: '' }, { withCredentials: true });
      await fetchProfiles();
    } catch {}
    setSaving(false);
  };

  const loadHistory = async () => {
    if (!selectedProfile) return;
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/watch-history`, { params: { profile_id: selectedProfile.id }, withCredentials: true });
      setWatchHistoryItems(data);
    } catch { setWatchHistoryItems([]); }
    setHistoryLoading(false);
  };

  const deleteProfile = async () => {
    if (!selectedProfile) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le profil "${selectedProfile.name}" ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/profiles/${selectedProfile.id}`, { withCredentials: true });
      await fetchProfiles();
      setSubPage(null);
      setSelectedProfile(null);
    } catch {}
  };

  const removeFromHistory = async (contentId) => {
    if (!selectedProfile) return;
    try {
      await axios.delete(`${API}/watch-history/${contentId}`, { params: { profile_id: selectedProfile.id }, withCredentials: true });
      setWatchHistoryItems(prev => prev.filter(item => item.id !== contentId));
    } catch {}
  };

  // ── SUB-PAGES ──

  const renderManageProfile = () => (
    <div>
      <button onClick={() => { setSubPage(null); setSelectedProfile(null); }} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={18} /> Retour
      </button>
      <h2 className="text-3xl font-extrabold text-white mb-2">Gérez votre profil et vos préférences</h2>

      {/* Profile info card — click to edit */}
      <div className="mt-8 bg-white/[0.03] border border-white/10 rounded-xl p-6 cursor-pointer hover:bg-white/[0.05] transition-colors" onClick={() => setSubPage('edit')}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-lg overflow-hidden flex-none bg-[#1a0e2e]">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=112`}
              alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <p className="text-lg font-bold text-white">{selectedProfile?.name}</p>
            <p className="text-xs text-white/40 mt-0.5">Modifiez vos informations personnelles et de contact</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </div>
      </div>

      {/* Avatar */}
      <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-xl">
        <button onClick={() => { setAvatarReturn('manage'); setSubPage('avatars'); }} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-change-avatar">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-none bg-[#1a0e2e]">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=80`}
              alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Modifier mon avatar</p>
            <p className="text-xs text-white/40 mt-0.5">Choisissez un nouvel avatar pour ce profil parmi la galerie</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>

      {/* Actions */}
      <h3 className="text-lg font-bold text-white mt-8 mb-4">Confidentialité liée au visionnage</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
        <button onClick={() => setSubPage('lock')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-lock-profile">
          <Lock size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Verrouillage du profil</p>
            <p className="text-xs text-white/40 mt-0.5">Exigez un code PIN pour accéder à ce profil</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => { setSubPage('history'); loadHistory(); }} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-history">
          <Clock size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Historique</p>
            <p className="text-xs text-white/40 mt-0.5">Gérez votre historique de lecture et les catégories d'âge</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>

      {/* Privacy & Data */}
      <h3 className="text-lg font-bold text-white mt-8 mb-4">Données et confidentialité</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
        <button onClick={() => setSubPage('privacy')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-privacy">
          <FileText size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Paramètres de données et de confidentialité</p>
            <p className="text-xs text-white/40 mt-0.5">Gérez vos préférences publicitaires et de données</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>

      {/* Preferences */}
      <h3 className="text-lg font-bold text-white mt-10 mb-4">Préférences</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
        <button onClick={() => setSubPage('languages')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-languages">
          <Languages size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Langues</p>
            <p className="text-xs text-white/40 mt-0.5">Définissez les langues pour l'application et l'audio</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => setSubPage('parental')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-parental">
          <ShieldAlert size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Contrôle parental</p>
            <p className="text-xs text-white/40 mt-0.5">Modifiez la catégorie d'âge et les restrictions</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => setSubPage('subtitles')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-subtitles">
          <Subtitles size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Affichage des sous-titres</p>
            <p className="text-xs text-white/40 mt-0.5">Personnalisez l'apparence des sous-titres</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => setSubPage('playback')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-playback">
          <Play size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Paramètres de lecture</p>
            <p className="text-xs text-white/40 mt-0.5">Gérez la lecture automatique et la qualité audio/vidéo</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => setSubPage('filters')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid="btn-filters">
          <Shield size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Filtres de contenu</p>
            <p className="text-xs text-white/40 mt-0.5">Choisissez les filtres appliqués par défaut lors du visionnage</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>

      {/* Delete profile */}
      {profiles[0]?.id === selectedProfile?.id ? (
        <div className="mt-10 bg-white/[0.03] border border-white/10 rounded-xl">
          <div className="flex items-center justify-center gap-3 px-6 py-5 w-full" data-testid="primary-profile-notice">
            <Shield size={20} className="text-white/40" />
            <span className="text-sm font-semibold text-white/40">Profil principal du compte — impossible à supprimer</span>
          </div>
        </div>
      ) : (
        <div className="mt-10 bg-white/[0.03] border border-red-500/20 rounded-xl">
          <button onClick={deleteProfile} className="flex items-center justify-center gap-3 px-6 py-5 w-full hover:bg-red-500/10 transition-colors" data-testid="btn-delete-profile">
            <Trash2 size={20} className="text-red-400" />
            <span className="text-sm font-semibold text-red-400">Supprimer le profil</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderLockProfile = () => (
    <div>
      <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={18} /> Retour
      </button>
      <h2 className="text-3xl font-extrabold text-white mb-2">Verrouillez votre profil</h2>
      <div className="flex items-center gap-2 mt-2 mb-8">
        <span className="text-white/50 text-sm">Pour</span>
        <div className="w-8 h-8 rounded-full overflow-hidden">
          <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
        </div>
        <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
      </div>

      <p className="text-white/50 text-sm mb-8 max-w-lg">
        Rendez ce profil privé en ajoutant un code PIN à 4 chiffres qui devra être saisi pour y accéder.
      </p>

      {selectedProfile?.pin ? (
        <div className="space-y-4">
          <p className="text-green-400 text-sm font-medium">Ce profil est verrouillé par un code PIN.</p>
          <button onClick={removePIN} disabled={saving}
            className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-lg font-semibold transition-colors">
            {saving ? 'Suppression...' : 'Supprimer le verrouillage'}
          </button>
        </div>
      ) : (
        <div className="space-y-4 max-w-xs">
          <div className="relative">
            <input
              type={showPin ? 'text' : 'password'}
              value={pinInput}
              onChange={e => { if (/^\d{0,4}$/.test(e.target.value)) setPinInput(e.target.value); }}
              placeholder="Code PIN à 4 chiffres"
              maxLength={4}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-sm placeholder:tracking-normal focus:outline-none focus:border-[#E9641A]"
              data-testid="pin-input"
            />
            <button onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
              {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button onClick={savePIN} disabled={pinInput.length !== 4 || saving}
            className="w-full py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-40">
            {saving ? 'Sauvegarde...' : 'Verrouiller le profil'}
          </button>
          <p className="text-[11px] text-white/30 mt-4">
            Remarque : Vous devrez saisir le mot de passe du compte en cas de changements concernant le verrouillage du profil.
          </p>
        </div>
      )}
    </div>
  );

  const renderLanguages = () => {
    const toggleLang = (lang) => {
      setAudioLangs(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
    };

    const saveLangs = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, { display_language: displayLang, preferred_languages: audioLangs }, { withCredentials: true });
        await fetchProfiles();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-2">Configuration des préférences de langue</h2>
        <div className="flex items-center gap-2 mt-2 mb-8">
          <span className="text-white/50 text-sm">Pour</span>
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
        </div>

        <div className="max-w-lg space-y-8">
          <div>
            <label className="text-sm text-white/60 mb-2 block">Langue d'affichage</label>
            <select value={displayLang} onChange={e => setDisplayLang(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
              {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <p className="text-[11px] text-white/30 mt-2">Cette section permet de gérer le texte affiché. Tout changement modifiera également la langue par défaut pour l'audio et les sous-titres.</p>
          </div>

          <div className="border-t border-white/10 pt-6">
            <h3 className="text-lg font-bold text-white mb-2">Langues audio et sous-titres</h3>
            <p className="text-xs text-white/40 mb-4">En choisissant les langues dans lesquelles vous regardez vos films et séries, vous nous aidez à mieux configurer l'audio et les sous-titres.</p>
            <div className="space-y-2">
              {LANGUAGES.map(lang => (
                <label key={lang} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-white/5 px-3 rounded-lg transition-colors">
                  <input type="checkbox" checked={audioLangs.includes(lang)} onChange={() => toggleLang(lang)}
                    className="w-4 h-4 rounded border-white/20 text-[#E9641A] focus:ring-[#E9641A] bg-transparent" />
                  <span className={`text-sm ${audioLangs.includes(lang) ? 'text-white' : 'text-white/50'}`}>{lang}</span>
                </label>
              ))}
            </div>
          </div>

          <button onClick={saveLangs} disabled={saving}
            className="px-8 py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    );
  };

  const renderParentalControl = () => {
    const ages = ['Tout public', '7+', '10+', '12+', '16+', '18+'];

    const saveParental = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, { max_age_rating: maxAge }, { withCredentials: true });
        await fetchProfiles();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-2">Contrôle parental</h2>
        <div className="flex items-center gap-2 mt-2 mb-8">
          <span className="text-white/50 text-sm">Pour</span>
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
        </div>

        <div className="max-w-lg">
          <p className="text-sm text-white/50 mb-6">Définissez la catégorie d'âge maximale pour ce profil. Les titres au-dessus de cette limite ne seront pas visibles.</p>
          <div className="space-y-2">
            {ages.map(age => (
              <button key={age} onClick={() => setMaxAge(age)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  maxAge === age ? 'border-[#E9641A] bg-[#E9641A]/10 text-white' : 'border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/5'
                }`}>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${maxAge === age ? 'border-[#E9641A]' : 'border-white/20'}`}>
                  {maxAge === age && <div className="w-2.5 h-2.5 rounded-full bg-[#E9641A]" />}
                </div>
                <span className="text-sm font-medium">{age}</span>
              </button>
            ))}
          </div>
          <button onClick={saveParental} disabled={saving}
            className="mt-6 px-8 py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    );
  };

  // ── MAIN TAB CONTENT ──

  const renderSubtitles = () => {
    const saveSubtitles = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, {
          subtitle_settings: { size: subtitleSize, font: subtitleFont, shadow: subtitleShadow, background: subtitleBg }
        }, { withCredentials: true });
        await fetchProfiles();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-2">Affichage des sous-titres</h2>
        <div className="flex items-center gap-2 mt-2 mb-4">
          <span className="text-white/50 text-sm">Pour</span>
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
        </div>
        <p className="text-sm text-white/40 mb-8">Changez l'affichage des sous-titres pour le profil sélectionné sur tous les appareils compatibles.</p>

        {/* Preview */}
        <div className="w-full max-w-2xl aspect-video bg-gradient-to-r from-purple-900 via-red-900 to-purple-800 rounded-xl mb-8 flex items-end justify-center pb-6">
          <p className="text-white text-center px-4" style={{
            fontSize: subtitleSize === 'Petit' ? '14px' : subtitleSize === 'Grand' ? '22px' : '18px',
            fontFamily: subtitleFont === 'Arial' ? 'Arial' : subtitleFont === 'Courier' ? 'Courier New' : 'sans-serif',
            textShadow: subtitleShadow === 'Ombre portée' ? '2px 2px 4px rgba(0,0,0,0.8)' : subtitleShadow === 'Contour' ? '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' : 'none',
            backgroundColor: subtitleBg === 'Noir' ? 'rgba(0,0,0,0.7)' : subtitleBg === 'Gris' ? 'rgba(128,128,128,0.5)' : 'transparent',
            padding: '4px 8px', borderRadius: '4px',
          }}>
            Ces paramètres affectent les sous-titres de tous les appareils pris en charge.
          </p>
        </div>

        <div className="max-w-lg space-y-6">
          <div>
            <label className="text-sm text-white/60 mb-2 block">Taille du texte</label>
            <select value={subtitleSize} onChange={e => setSubtitleSize(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
              {['Petit', 'Moyen', 'Grand'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-white/60 mb-2 block">Police</label>
            <select value={subtitleFont} onChange={e => setSubtitleFont(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
              {['Block', 'Arial', 'Courier', 'Times'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-white/60 mb-2 block">Ombre</label>
            <select value={subtitleShadow} onChange={e => setSubtitleShadow(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
              {['Sans', 'Ombre portée', 'Contour', 'Relief'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">Arrière-plan</label>
              <select value={subtitleBg} onChange={e => setSubtitleBg(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
                {['Sans', 'Noir', 'Gris', 'Blanc'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
          <button onClick={saveSubtitles} disabled={saving}
            className="w-full py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  };

  const renderPlayback = () => {
    const savePlayback = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, {
          playback_settings: { autoplay_next: autoplayNext, autoplay_previews: autoplayPreviews, data_usage: dataUsage }
        }, { withCredentials: true });
        await fetchProfiles();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    const dataOptions = [
      { id: 'Auto', label: 'Auto', desc: 'Qualité audio, vidéo et utilisation des données par défaut' },
      { id: 'Faible', label: 'Faible', desc: 'Qualité audio et vidéo de base, jusqu\'à 0,3 Go par heure' },
      { id: 'Moyenne', label: 'Moyenne', desc: 'Qualité audio et vidéo standard, jusqu\'à 0,7 Go par heure' },
      { id: 'Élevée', label: 'Élevée', desc: 'Qualité audio et vidéo optimale, jusqu\'à 3 Go par heure' },
    ];

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-2">Paramètres de lecture</h2>
        <div className="flex items-center gap-2 mt-2 mb-8">
          <span className="text-white/50 text-sm">Pour</span>
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
        </div>

        <div className="max-w-lg space-y-8">
          {/* Autoplay */}
          <div>
            <h3 className="text-base font-semibold text-white/60 mb-4">Lecture automatique et transitions</h3>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="text-sm font-semibold text-white">Lecture automatique de l'épisode suivant</p>
                  <p className="text-xs text-white/40 mt-0.5">Sur tous les appareils</p>
                </div>
                <button onClick={() => setAutoplayNext(!autoplayNext)}
                  className={`w-12 h-7 rounded-full transition-colors relative ${autoplayNext ? 'bg-[#E9641A]' : 'bg-white/20'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${autoplayNext ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="text-sm font-semibold text-white">Lecture automatique des aperçus</p>
                  <p className="text-xs text-white/40 mt-0.5">Sur tous les appareils</p>
                </div>
                <button onClick={() => setAutoplayPreviews(!autoplayPreviews)}
                  className={`w-12 h-7 rounded-full transition-colors relative ${autoplayPreviews ? 'bg-[#E9641A]' : 'bg-white/20'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${autoplayPreviews ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Data usage */}
          <div>
            <h3 className="text-base font-semibold text-white/60 mb-4">Utilisation des données par écran</h3>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
              {dataOptions.map(opt => (
                <button key={opt.id} onClick={() => setDataUsage(opt.id)}
                  className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors">
                  <div className={`w-5 h-5 rounded-full border-2 flex-none mt-0.5 flex items-center justify-center ${dataUsage === opt.id ? 'border-[#E9641A]' : 'border-white/20'}`}>
                    {dataUsage === opt.id && <div className="w-2.5 h-2.5 rounded-full bg-[#E9641A]" />}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${dataUsage === opt.id ? 'text-white' : 'text-white/60'}`}>{opt.label}</p>
                    <p className="text-xs text-white/40 mt-0.5">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button onClick={savePlayback} disabled={saving}
            className="w-full py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  };

  const renderEditProfile = () => {
    const saveEditProfile = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, { name: editName, gender }, { withCredentials: true });
        await fetchProfiles();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-8">Modifiez votre profil</h2>

        <div className="flex gap-6 items-start mb-8">
          <button onClick={() => { setAvatarReturn('edit'); setSubPage('avatars'); }} className="relative group flex-none">
            <div className="w-24 h-28 rounded-lg overflow-hidden">
              <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=1a0e2e&color=E9641A&size=192`}
                alt="" className="w-full h-full object-cover group-hover:brightness-75 transition-all" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Pencil size={20} className="text-white drop-shadow-lg" />
            </div>
          </button>
          <div className="flex-1 space-y-4 max-w-md">
            <div>
              <label className="text-xs text-white/40 mb-1 block">Nom de profil</label>
              <input value={editName} onChange={e => setEditName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]" />
            </div>
            <div>
              <label className="text-xs text-white/40 mb-1 block">Genre</label>
              <select value={gender} onChange={e => setGender(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#E9641A]">
                <option value="">Non spécifié</option>
                <option value="homme">Homme</option>
                <option value="femme">Femme</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>
        </div>

        <button onClick={saveEditProfile} disabled={saving}
          className="w-full max-w-md py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
      </div>
    );
  };

  const renderAvatarGallery = () => {
    const selectAvatar = async (url) => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, { avatar_url: url }, { withCredentials: true });
        await fetchProfiles();
        await checkAuth();
        setSelectedProfile(prev => ({ ...prev, avatar_url: url }));
        setSubPage(avatarReturn);
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage(avatarReturn)} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-1">Choisissez une icône de profil</h2>
        <div className="flex items-center gap-2 mb-8">
          <span className="text-white/50 text-sm">Pour {selectedProfile?.name}</span>
          <div className="w-7 h-7 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=56`} alt="" className="w-full h-full object-cover" />
          </div>
        </div>

        {avatarCategories.length > 0 ? (
          <div className="space-y-8">
            {avatarCategories.map(cat => (
              <div key={cat.name}>
                <h3 className="text-base font-bold text-white mb-3">{cat.name}</h3>
                <div className="flex gap-2 flex-wrap">
                  {cat.avatars.map((url, idx) => (
                    <button key={idx} onClick={() => selectAvatar(url)}
                      className="w-20 h-20 rounded-lg overflow-hidden border-2 border-transparent hover:border-[#E9641A] transition-all flex-none">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/40 text-sm">Aucun avatar disponible. L'administrateur peut en ajouter depuis le dashboard.</p>
        )}
      </div>
    );
  };

  const renderProfileFilters = () => {
    const toggleFilter = (key) => {
      setProfileFilters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const saveFilters = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/profiles/${selectedProfile.id}`, { default_filters: profileFilters }, { withCredentials: true });
        // Clear player's local cache so it picks up the new profile defaults on next visit
        localStorage.removeItem(`player_prefs_${selectedProfile.id}`);
        await fetchProfiles();
        await checkAuth();
        setSubPage('manage');
      } catch {}
      setSaving(false);
    };

    return (
      <div>
        <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Retour
        </button>
        <h2 className="text-3xl font-extrabold text-white mb-2">Filtres de contenu par défaut</h2>
        <div className="flex items-center gap-2 mt-2 mb-4">
          <span className="text-white/50 text-sm">Pour</span>
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
        </div>
        <p className="text-sm text-white/40 mb-8 max-w-lg">Ces filtres seront appliqués par défaut lors du visionnage. Vous pouvez les modifier temporairement dans le lecteur vidéo.</p>

        <div className="max-w-lg bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
          {adminFilters.map(f => (
            <div key={f.id} className="flex items-center justify-between px-6 py-5">
              <div>
                <span className="text-sm font-semibold text-white">{f.name}</span>
                <p className="text-[10px] text-white/30 mt-0.5">{f.description}</p>
              </div>
              <button onClick={() => toggleFilter(f.id)}
                className={`w-12 h-7 rounded-full transition-colors relative ${profileFilters[f.id] ? 'bg-[#E9641A]' : 'bg-white/20'}`}>
                <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${profileFilters[f.id] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>

        <button onClick={saveFilters} disabled={saving}
          className="mt-6 px-8 py-3 bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-bold transition-colors disabled:opacity-50">
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
      </div>
    );
  };

  const renderHistory = () => (
    <div>
      <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={18} /> Retour
      </button>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-3xl font-extrabold text-white">Historique du profil {selectedProfile?.name}</h2>
        <div className="w-12 h-12 rounded-lg overflow-hidden">
          <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=96`} alt="" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="flex gap-4 mb-6 border-b border-white/10 pb-3">
        <span className="text-sm font-semibold text-white border-b-2 border-white pb-3">Titres vus</span>
      </div>

      {historyLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
        </div>
      ) : watchHistoryItems.length === 0 ? (
        <p className="text-white/40 text-sm py-8">Aucun titre visionné pour ce profil.</p>
      ) : (
        <div className="divide-y divide-white/5">
          {watchHistoryItems.map(item => (
            <div key={item.id} className="flex items-center gap-4 py-4" data-testid={`history-item-${item.id}`}>
              <span className="text-xs text-white/40 w-20 flex-none">
                {item.last_watched ? new Date(item.last_watched).toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric', year: '2-digit' }) : ''}
              </span>
              <span className="text-sm text-[#75D4D5] flex-1 hover:underline cursor-pointer">{item.title}</span>
              <span className="text-xs text-white/40 hidden md:block">Signaler un problème</span>
              <button onClick={() => removeFromHistory(item.id)}
                className="p-1.5 text-white/30 hover:text-red-400 transition-colors" title="Supprimer de l'historique">
                <Ban size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPrivacy = () => (
    <div>
      <button onClick={() => setSubPage('manage')} className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={18} /> Retour
      </button>
      <h2 className="text-3xl font-extrabold text-white mb-2">Gérez les paramètres de données et de confidentialité</h2>
      <div className="flex items-center gap-2 mt-2 mb-8">
        <span className="text-white/50 text-sm">Pour</span>
        <div className="w-8 h-8 rounded-full overflow-hidden">
          <img src={selectedProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedProfile?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
        </div>
        <span className="text-white/70 text-sm font-medium">{selectedProfile?.name}</span>
      </div>

      {/* Info banner */}
      <div className="bg-[#75D4D5]/10 border border-[#75D4D5]/20 rounded-xl p-5 mb-8">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-[#75D4D5] flex-none mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white">Activez tous les paramètres pour une meilleure expérience</p>
            <p className="text-xs text-white/50 mt-1">En partageant vos données, vous aidez Maya+ et ses partenaires à vous proposer du contenu plus pertinent.</p>
            <button className="mt-3 px-6 py-2.5 bg-white text-[#11041F] rounded-lg text-sm font-bold hover:bg-white/90 transition-colors">
              Activer
            </button>
          </div>
        </div>
      </div>

      {/* Viewing privacy */}
      <h3 className="text-base text-white/50 mb-4">Confidentialité liée au visionnage</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5 mb-8">
        <button onClick={() => setSubPage('lock')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors">
          <Lock size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Verrouillage du profil</p>
            <p className="text-xs text-white/40 mt-0.5">Exigez un code PIN pour accéder à ce profil</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
        <button onClick={() => { setSubPage('history'); loadHistory(); }} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors">
          <Clock size={20} className="text-white/40" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-white">Historique</p>
            <p className="text-xs text-white/40 mt-0.5">Gérez votre historique de lecture et les catégories d'âge</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>

      {/* Ad partner data */}
      <h3 className="text-base text-white/50 mb-4">Paramètres de données des partenaires publicitaires</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5 mb-8">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-white">Activer les finalités de consentement des utilisateurs</p>
              <p className="text-xs text-white/40 mt-1 max-w-md">
                Cette fonctionnalité autorise Maya+ et ses partenaires à personnaliser vos publicités, à mesurer les performances, à stocker et accéder à des informations sur vos appareils.
              </p>
            </div>
            <button onClick={() => setConsentAds(!consentAds)}
              className={`w-12 h-7 rounded-full transition-colors relative flex-none ${consentAds ? 'bg-[#75D4D5]' : 'bg-white/20'}`}>
              <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${consentAds ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Maya+ data settings */}
      <h3 className="text-base text-white/50 mb-4">Paramètres de données Maya+</h3>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-white">Activer le ciblage publicitaire comportemental</p>
              <p className="text-xs text-white/40 mt-1 max-w-md">
                Cette fonctionnalité utilise votre historique et vos interactions sur Maya+ pour sélectionner des publicités pertinentes. Si vous la désactivez, vous verrez toujours des publicités, mais elles ne seront pas ciblées.
              </p>
            </div>
            <button onClick={() => setBehavioralAds(!behavioralAds)}
              className={`w-12 h-7 rounded-full transition-colors relative flex-none ${behavioralAds ? 'bg-[#75D4D5]' : 'bg-white/20'}`}>
              <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${behavioralAds ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfilesTab = () => {
    if (subPage === 'manage' && selectedProfile) return renderManageProfile();
    if (subPage === 'lock' && selectedProfile) return renderLockProfile();
    if (subPage === 'languages' && selectedProfile) return renderLanguages();
    if (subPage === 'parental' && selectedProfile) return renderParentalControl();
    if (subPage === 'subtitles' && selectedProfile) return renderSubtitles();
    if (subPage === 'playback' && selectedProfile) return renderPlayback();
    if (subPage === 'history' && selectedProfile) return renderHistory();
    if (subPage === 'privacy' && selectedProfile) return renderPrivacy();
    if (subPage === 'edit' && selectedProfile) return renderEditProfile();
    if (subPage === 'avatars' && selectedProfile) return renderAvatarGallery();
    if (subPage === 'filters' && selectedProfile) return renderProfileFilters();

    return (
      <div>
        <h2 className="text-3xl font-extrabold text-white mb-2">Profils</h2>

        {/* Parental controls + Transfer */}
        <h3 className="text-base text-white/50 mb-6">Contrôle parental et permissions</h3>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5 mb-10">
          <button className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors">
            <ShieldAlert size={20} className="text-white/40" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-white">Ajuster le contrôle parental</p>
              <p className="text-xs text-white/40 mt-0.5">Définissez la catégorie d'âge, bloquez des titres</p>
            </div>
            <ChevronRight size={18} className="text-white/30" />
          </button>
          <button className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors">
            <Monitor size={20} className="text-white/40" />
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-white">Transférer un profil</p>
              <p className="text-xs text-white/40 mt-0.5">Copiez un profil vers un autre compte</p>
            </div>
            <ChevronRight size={18} className="text-white/30" />
          </button>
        </div>

        {/* Profile list */}
        <h3 className="text-base text-white/50 mb-4">Paramètres de profil</h3>
        <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
          {profiles.map((profile, idx) => (
            <button key={profile.id} onClick={() => openProfileManage(profile)}
              className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors" data-testid={`profile-settings-${profile.id}`}>
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-none bg-[#1a0e2e]">
                <img src={profile.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=E9641A&color=fff&size=96`}
                  alt="" className="w-full h-full object-cover" />
              </div>
              <span className="text-sm font-semibold text-white flex-1 text-left">{profile.name}</span>
              {(activeProfile?.id === profile.id || (!activeProfile && idx === 0)) && (
                <span className="px-2.5 py-1 bg-white/10 text-white/50 text-[10px] font-medium rounded border border-white/10">Votre profil</span>
              )}
              <ChevronRight size={18} className="text-white/30" />
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderOverviewTab = () => (
    <div>
      <h2 className="text-3xl font-extrabold text-white mb-6">Présentation</h2>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/50">Email</span>
          <span className="text-sm text-white">{user?.email}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/50">Nom</span>
          <span className="text-sm text-white">{user?.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/50">Abonnement</span>
          <span className="text-sm text-white capitalize">{user?.subscription_plan || 'Gratuit'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/50">Profils</span>
          <span className="text-sm text-white">{profiles.length} profil(s)</span>
        </div>
      </div>
    </div>
  );

  const renderSubscriptionTab = () => <SubscriptionSection navigate={navigate} />;

  const renderSecurityTab = () => (
    <div>
      <h2 className="text-3xl font-extrabold text-white mb-6">Sécurité</h2>
      <div className="bg-white/[0.03] border border-white/10 rounded-xl divide-y divide-white/5">
        <div className="flex items-center gap-4 px-6 py-5">
          <Lock size={20} className="text-white/40" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Changer le mot de passe</p>
            <p className="text-xs text-white/40 mt-0.5">Modifiez votre mot de passe de connexion</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </div>
        <button onClick={() => changeTab('devices')} className="flex items-center gap-4 px-6 py-5 w-full hover:bg-white/5 transition-colors text-left" data-testid="security-devices-btn">
          <Monitor size={20} className="text-white/40" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-white">Déconnecter tous les appareils</p>
            <p className="text-xs text-white/40 mt-0.5">Déconnectez-vous de tous les appareils sauf celui-ci</p>
          </div>
          <ChevronRight size={18} className="text-white/30" />
        </button>
      </div>
    </div>
  );

  const renderDevicesTab = () => <DevicesTab />;

  const renderContent = () => {
    switch (tab) {
      case 'overview': return renderOverviewTab();
      case 'subscription': return renderSubscriptionTab();
      case 'security': return renderSecurityTab();
      case 'devices': return renderDevicesTab();
      case 'profiles': return renderProfilesTab();
      default: return renderProfilesTab();
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#11041F]">
      <div className="w-8 h-8 border-2 border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#11041F]" data-testid="account-settings-page">
      {/* Top bar */}
      <div className="border-b border-white/10 px-8 py-4 flex items-center justify-between">
        <img src={LOGO_URL} alt="Maya+" className="h-7" />
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded overflow-hidden">
            <img src={activeProfile?.avatar_url || user?.profiles?.find(p => p.id === activeProfile?.id)?.avatar_url || user?.profiles?.[0]?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeProfile?.name || user?.name || '')}&background=E9641A&color=fff&size=64`} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      <div className="flex max-w-6xl mx-auto px-4 py-8">
        {/* Sidebar */}
        <div className="w-56 flex-none pr-8">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft size={16} /> Retour à Maya+
          </button>
          <nav className="space-y-1">
            {SIDEBAR_ITEMS.map(item => (
              <button key={item.id} onClick={() => changeTab(item.id)} data-testid={`settings-tab-${item.id}`}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  tab === item.id ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'
                }`}>
                <item.icon size={18} />
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
